import React, { useState } from "react";
import {
  Form, Input, Button, Card, Row, Col, Select, Upload, message, Tabs, Typography, Space, Switch, Tooltip, Modal, Alert, Tag, Collapse, List,
} from "antd";
import {
  PlusOutlined, DeleteOutlined, UploadOutlined, SendOutlined, InfoCircleOutlined, CheckCircleOutlined, ExclamationCircleOutlined,
} from "@ant-design/icons";
import apiClient from "services/apiClient";
import { useMyContext } from "Context/MyContextProvider";
import { ROW_GUTTER } from "constants/ThemeConstant";
import "./CarouselTemplateCreator.css";
import CarouselPreview from "../carouselPreview";
import Flex from "components/shared-components/Flex";
import { languages } from "../../CustomUtils.js/TemplateUtils";

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;
const { Title } = Typography;
// Button types available
const buttonTypes = [
  { value: "QUICK_REPLY", label: "Quick Reply" },
  { value: "URL", label: "Visit Website" },
  { value: "PHONE_NUMBER", label: "Call Phone" },
];

const CarouselTemplateCreator = () => {
  const { navigate, appId, api } = useMyContext();

  // Form state
  const [form] = Form.useForm();
  const [isLoop, setIsLoop] = useState(false);
  const [loading, setLoading] = useState(false);

  // Template basic info
  const [templateData, setTemplateData] = useState({
    name: "",
    category: "MARKETING",
    language: "en_US",
    bodyText: "",
    footerText: "",
  });

  // Template image for submission and card headers
  const [templateImage, setTemplateImage] = useState({
    mediaUrl: "",
    mediaHandle: "",
    type: "IMAGE", // Default header type for all cards
  });

  // Carousel cards
  const [cards, setCards] = useState([
    {
      id: 1,
      header: {
        type: "IMAGE",
        mediaUrl: "",
        mediaHandle: "",
        text: "",
      },
      body: "",
      buttons: [],
    },
  ]);

  // Helper functions for dynamic variables
  const extractVariables = (text) => {
    const variableRegex = /{{(\d+)}}/g;
    const matches = [];
    let match;
    while ((match = variableRegex.exec(text)) !== null) {
      matches.push(parseInt(match[1], 10));
    }
    return matches.sort((a, b) => a - b);
  };

  const addVariable = (text, setText) => {
    const existingVariables = extractVariables(text);
    const nextNumber =
      existingVariables.length === 0 ? 1 : Math.max(...existingVariables) + 1;
    const newVariable = `{{${nextNumber}}}`;
    const updatedText = text ? `${text} ${newVariable}` : newVariable;
    setText(updatedText);
  };

  // Add single variable for URL (only {{1}} allowed)
  const addUrlVariable = (url, setUrl) => {
    if (url.includes("{{1}}")) {
      message.warning("Only one variable {{1}} is allowed per URL");
      return;
    }
    const newUrl = url ? `${url}{{1}}` : "{{1}}";
    setUrl(newUrl);
  };

  const validateVariableSequence = (text) => {
    const variables = extractVariables(text);
    if (variables.length === 0) return true;

    // Check if variables start from 1 and are sequential
    for (let i = 0; i < variables.length; i++) {
      if (variables[i] !== i + 1) {
        return false;
      }
    }
    return true;
  };

  // Add new card
  const addCard = () => {
    if (cards.length >= 10) {
      message.warning("Maximum 10 cards allowed in a carousel template");
      return;
    }

    const newCard = {
      id: Date.now(),
      body: "",
      buttons: [],
    };

    setCards([...cards, newCard]);
  };

  // Remove card
  const removeCard = (cardId) => {
    if (cards.length <= 1) {
      message.warning("At least one card is required");
      return;
    }
    setCards(cards.filter((card) => card.id !== cardId));
  };

  // Update card data
  const updateCard = (cardId, field, value) => {
    setCards(
      cards.map((card) =>
        card.id === cardId ? { ...card, [field]: value } : card
      )
    );
  };

  // Update template header type (applies to all cards)
  const updateTemplateHeaderType = (type) => {
    setTemplateImage((prev) => ({
      ...prev,
      type: type,
      // Clear media when switching to TEXT
      ...(type === "TEXT" ? { mediaUrl: "", mediaHandle: "" } : {}),
    }));
  };

  // Update template header text
  const updateTemplateHeaderText = (text) => {
    setTemplateImage((prev) => ({
      ...prev,
      text: text,
    }));
  };

  // Add button to card
  const addButton = (cardId) => {
    const card = cards.find((c) => c.id === cardId);
    if (card.buttons.length >= 2) {
      message.warning("Maximum 2 buttons allowed per card");
      return;
    }

    const newButton = {
      id: Date.now(),
      type: "QUICK_REPLY",
      text: "",
      url: "",
      urlType: "STATIC", // STATIC or DYNAMIC
      phoneNumber: "",
    };

    updateCard(cardId, "buttons", [...card.buttons, newButton]);
  };

  // Remove button from card
  const removeButton = (cardId, buttonId) => {
    const card = cards.find((c) => c.id === cardId);
    const updatedButtons = card.buttons.filter((btn) => btn.id !== buttonId);
    updateCard(cardId, "buttons", updatedButtons);
  };

  // Update button
  const updateButton = (cardId, buttonId, field, value) => {
    const card = cards.find((c) => c.id === cardId);

    // Check for PHONE_NUMBER button limit when changing button type
    if (field === "type" && value === "PHONE_NUMBER") {
      const existingPhoneButtons = card.buttons.filter(
        (btn) => btn.type === "PHONE_NUMBER" && btn.id !== buttonId
      );
      if (existingPhoneButtons.length >= 1) {
        message.warning("Maximum 1 call button allowed per card");
        return;
      }
    }

    // If button type is changing to URL, set default urlType to STATIC
    if (field === "type" && value === "URL") {
      const updatedButtons = card.buttons.map((btn) =>
        btn.id === buttonId
          ? { ...btn, [field]: value, urlType: "STATIC", url: "" }
          : btn
      );
      updateCard(cardId, "buttons", updatedButtons);
      return;
    }

    const updatedButtons = card.buttons.map((btn) =>
      btn.id === buttonId ? { ...btn, [field]: value } : btn
    );
    updateCard(cardId, "buttons", updatedButtons);
  };

  // Template image upload handler (used for all card headers)
  const handleTemplateImageUpload = async (file) => {
    const isValidType =
      templateImage.type === "IMAGE"
        ? ["image/jpeg", "image/png", "image/jpg"].includes(file.type)
        : ["video/mp4"].includes(file.type);

    if (!isValidType) {
      const allowedTypes =
        templateImage.type === "IMAGE" ? "JPG and PNG" : "MP4";
      message.error(
        `Only ${allowedTypes} files are allowed for ${templateImage.type.toLowerCase()}`
      );
      return false;
    }

    const formData = new FormData();
    formData.append("media", file);

    formData.append("appId", appId);

    try {
      setLoading(true);
      const response = await apiClient.post(`${api}upload-session`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setTemplateImage((prev) => ({
        ...prev,
        mediaHandle: response.data,
        mediaUrl: URL.createObjectURL(file),
      }));

      message.success(
        `Template ${templateImage.type.toLowerCase()} uploaded successfully`
      );
    } catch (error) {
      console.error("Upload error:", error);
      message.error(
        `Failed to upload template ${templateImage.type.toLowerCase()}`
      );
    } finally {
      setLoading(false);
    }

    return false; // Prevent default upload
  };

  const isValidTemplateName = (name) => {
    // Meta: 3-512 chars, lowercase, numbers, underscores, must start with a letter
    return /^[a-z][a-z0-9_]{2,511}$/.test(name);
  };

  // Validate template data
  const validateTemplate = () => {
    const errors = [];

    if (!templateData.name.trim()) {
      errors.push("Template name is required");
    } else if (!isValidTemplateName(templateData.name.trim())) {
      errors.push(
        "Template name must start with a letter, use only lowercase letters, numbers, and underscores, and be 3-512 characters long (no spaces or special characters)"
      );
    }

    if (!templateData.name.trim()) {
      errors.push("Template name is required");
    }

    if (!templateData.bodyText.trim()) {
      errors.push("Body text is required");
    }

    // Validate body text variables
    if (
      templateData.bodyText &&
      !validateVariableSequence(templateData.bodyText)
    ) {
      errors.push(
        "Body text: Variables must start from {{1}} and be sequential"
      );
    }

    if (!templateImage.mediaHandle && templateImage.type !== "TEXT") {
      errors.push(
        `Template ${templateImage.type.toLowerCase()} is required for submission`
      );
    }

    if (templateImage.type === "TEXT" && !templateImage.text?.trim()) {
      errors.push("Template header text is required when using TEXT type");
    }

    if (cards.length === 0) {
      errors.push("At least one card is required");
    }

    cards.forEach((card, index) => {
      if (!card.body.trim()) {
        errors.push(`Card ${index + 1}: Body text is required`);
      }

      // Validate card body variables
      if (card.body && !validateVariableSequence(card.body)) {
        errors.push(
          `Card ${index + 1
          }: Body text variables must start from {{1}} and be sequential`
        );
      }

      // Validate button requirements
      if (card.buttons.length === 0) {
        errors.push(`Card ${index + 1}: At least 1 button is required`);
      }

      if (card.buttons.length > 2) {
        errors.push(`Card ${index + 1}: Maximum 2 buttons allowed per card`);
      }

      // Validate maximum 1 call button per card
      const phoneButtons = card.buttons.filter(
        (btn) => btn.type === "PHONE_NUMBER"
      );
      if (phoneButtons.length > 1) {
        errors.push(
          `Card ${index + 1}: Maximum 1 call button allowed per card`
        );
      }

      card.buttons.forEach((button, btnIndex) => {
        if (!button.text.trim()) {
          errors.push(
            `Card ${index + 1}, Button ${btnIndex + 1}: Button text is required`
          );
        }

        if (button.type === "URL" && !button.url.trim()) {
          errors.push(
            `Card ${index + 1}, Button ${btnIndex + 1}: URL is required`
          );
        }

        // Validate URL variables
        if (button.type === "URL" && button.urlType === "DYNAMIC") {
          if (!button.url.includes("{{1}}")) {
            errors.push(
              `Card ${index + 1}, Button ${btnIndex + 1
              }: Dynamic URL must contain {{1}} variable`
            );
          }
          const urlVariables = extractVariables(button.url);
          if (
            urlVariables.length > 1 ||
            (urlVariables.length === 1 && urlVariables[0] !== 1)
          ) {
            errors.push(
              `Card ${index + 1}, Button ${btnIndex + 1
              }: Dynamic URL can only contain {{1}} variable`
            );
          }
        }

        if (
          button.type === "URL" &&
          button.urlType === "STATIC" &&
          extractVariables(button.url).length > 0
        ) {
          errors.push(
            `Card ${index + 1}, Button ${btnIndex + 1
            }: Static URL cannot contain variables`
          );
        }

        if (button.type === "PHONE_NUMBER" && !button.phoneNumber.trim()) {
          errors.push(
            `Card ${index + 1}, Button ${btnIndex + 1
            }: Phone number is required`
          );
        }
      });
    });

    return errors;
  };

  // Submit template to Meta
  const submitTemplate = async () => {
    const errors = validateTemplate();

    if (errors.length > 0) {
      Modal.error({
        title: "Validation Errors",
        content: (
          <div>
            {errors.map((error, index) => (
              <div key={index} style={{ marginBottom: "8px" }}>
                <ExclamationCircleOutlined
                  style={{ color: "#ff4d4f", marginRight: "8px" }}
                />
                {error}
              </div>
            ))}
          </div>
        ),
      });
      return;
    }

    try {
      setLoading(true);
      // Build template structure for Meta API
      const bodyVariables = extractVariables(templateData.bodyText);
      const templatePayload = {
        messaging_product: "whatsapp",
        name: templateData.name.toLowerCase().replace(/\s+/g, "_"),
        language: templateData.language,
        category: "MARKETING",
        components: [
          // Body component
          {
            type: "BODY",
            text: templateData.bodyText,
            ...(bodyVariables.length > 0 && {
              example: {
                body_text: [
                  bodyVariables.map(
                    (_, index) => `Example ${index + 1}`
                  ),
                ],
              },
            }),
          },
          // Carousel component
          {
            type: "CAROUSEL",
            cards: cards.map((card, cardIdx) => {
              const cardBodyVariables = extractVariables(card.body);

              return {
                components: [
                  // Header component (using common template image/text)
                  templateImage.type === "TEXT"
                    ? {
                      type: "HEADER",
                      format: "TEXT",
                      text: templateImage.text,
                    }
                    : {
                      type: "HEADER",
                      format: templateImage.type,
                      example: {
                        header_handle: [templateImage.mediaHandle.h],
                      },
                    },
                  // Body component
                  {
                    type: "BODY",
                    text: card.body,
                    ...(cardBodyVariables.length > 0 && {
                      example: {
                        body_text: [
                          cardBodyVariables.map(
                            (_, varIdx) => `Card${cardIdx + 1}_Var${varIdx + 1}`
                          ),
                        ],
                      },
                    }),
                  },
                  // Buttons component
                  ...(card.buttons.length > 0
                    ? [
                      {
                        type: "BUTTONS",
                        buttons: card.buttons.map((button) => {
                          switch (button.type) {
                            case "URL":
                              const urlVariables = extractVariables(
                                button.url
                              );
                              return {
                                type: "URL",
                                text: button.text,
                                url: button.url,
                                ...(button.urlType === "DYNAMIC" &&
                                  urlVariables.length > 0 && {
                                  example: ["example_param"],
                                }),
                              };
                            case "PHONE_NUMBER":
                              return {
                                type: "PHONE_NUMBER",
                                text: button.text,
                                phone_number: button.phoneNumber,
                              };
                            default:
                              return {
                                type: "QUICK_REPLY",
                                text: button.text,
                              };
                          }
                        }),
                      },
                    ]
                    : []),
                ],
              };
            }),
          },
          // Footer component (if provided)
          ...(templateData.footerText
            ? [
              {
                type: "FOOTER",
                text: templateData.footerText,
              },
            ]
            : []),
        ],
      };
      const response = await apiClient.post(
        "messaging/templates",
        templatePayload
      );
      if (response.status === 200) {
        navigate(-1);
        message.success("Template submitted successfully to Meta!");
      } else {
        message.error("Failed to submit template to Meta.");
      }

      // Reset form
      setTemplateData({
        name: "",
        category: "MARKETING",
        language: "en_US",
        bodyText: "",
        footerText: "",
      });
      setTemplateImage({
        mediaUrl: "",
        mediaHandle: "",
      });
      setCards([
        {
          id: 1,
          body: "",
          buttons: [],
        },
      ]);
      form.resetFields();
    } catch (error) {
      setLoading(false);
      // Try to extract Meta error message
      let metaMsg = "Failed to submit template. Please try again.";
      const data = error?.response?.data;
      if (data) {
        if (typeof data === "string") {
          metaMsg = data;
        } else if (data?.error && typeof data.error === "string") {
          metaMsg = data.error;
        } else if (data?.error?.message) {
          metaMsg = data.error.message;
        } else if (data?.message) {
          metaMsg = data.message;
        }
      }
      Modal.error({
        title: "Submission Error",
        content: metaMsg,
      });
      // Do NOT navigate(-1) here
      return;
    } finally {
      setLoading(false);
      // Do not navigate(-1) here
    }
  };
  return (
    <div className="carousel-template-creator">
      <Row gutter={ROW_GUTTER}>
        <Col xs={24} lg={16}>
          <Card
            title={"Carousel Template"}
            extra={
              <Space>
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  loading={loading}
                  onClick={submitTemplate}
                >
                  Submit Template
                </Button>
              </Space>
            }
          >
            <Form
              form={form}
              layout="vertical"
              style={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}
            >
              {/* Basic Template Information */}
              <Card size="small" className="mb-3">
                <Row gutter={ROW_GUTTER}>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label="Template Name"
                      name="templateName"
                      required
                      help="lowercase letters, numbers, and underscores only"
                      rules={[
                        {
                          required: true,
                          message: "Template name is required",
                        },
                        {
                          pattern: /^[a-z][a-z0-9_]{2,511}$/,
                          message:
                            "Must start with a letter, use only lowercase letters, numbers, and underscores, and be 3-512 characters long",
                        },
                      ]}
                      validateTrigger={["onBlur", "onChange"]}
                    >
                      <Input
                        placeholder="e.g., summer_sale_carousel"
                        value={templateData.name}
                        onChange={(e) => {
                          let formatted = e.target.value
                            .toLowerCase()
                            .replace(/[\s-]+/g, "_")
                            .replace(/[^a-z0-9_]/g, "");

                          // Update both state and form value
                          setTemplateData((prev) => ({
                            ...prev,
                            name: formatted,
                          }));

                          // Update form field value
                          form.setFieldsValue({ templateName: formatted });
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item label="Category" required>
                      <Select
                        value={templateData.category}
                        disabled
                        onChange={(value) =>
                          setTemplateData((prev) => ({
                            ...prev,
                            category: value,
                          }))
                        }
                      >
                        <Option value="MARKETING">Marketing</Option>
                        <Option value="UTILITY">Utility</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item label="Language" required>
                      <Select
                        value={templateData.language}
                        onChange={(value) =>
                          setTemplateData((prev) => ({
                            ...prev,
                            language: value,
                          }))
                        }
                        showSearch
                        filterOption={(input, option) =>
                          option.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        {languages.map((lang) => (
                          <Option key={lang.value} value={lang.value}>
                            {lang.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={ROW_GUTTER}>
                  <Col xs={24} md={24}>
                    <Form.Item
                      label="Body Text"
                      required
                      help="Main message text that appears above the carousel. Use {{1}}, {{2}}, etc. for dynamic variables"
                    >
                      <TextArea
                        rows={3}
                        placeholder="Enter your main message text... Use {{1}}, {{2}} for variables"
                        value={templateData.bodyText}
                        onChange={(e) =>
                          setTemplateData((prev) => ({
                            ...prev,
                            bodyText: e.target.value,
                          }))
                        }
                        maxLength={1024}
                        showCount
                      />
                      <div
                        style={{
                          marginTop: "8px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Button
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={() =>
                            addVariable(templateData.bodyText, (newText) =>
                              setTemplateData((prev) => ({
                                ...prev,
                                bodyText: newText,
                              }))
                            )
                          }
                        >
                          Add Variable
                        </Button>
                        {extractVariables(templateData.bodyText).length > 0 && (
                          <Text type="secondary" style={{ fontSize: "12px" }}>
                            Variables:{" "}
                            {extractVariables(templateData.bodyText)
                              .map((v) => `{{${v}}}`)
                              .join(", ")}
                          </Text>
                        )}
                      </div>
                      <Text className="text-warning">
                        Note: Keep your message short, when variables are
                        replaced with real values, the final text must still fit
                        in <strong className="text-danger">1024</strong>{" "}
                        characters.
                      </Text>
                      {templateData.bodyText &&
                        !validateVariableSequence(templateData.bodyText) && (
                          <Alert
                            message="Variable Sequence Error"
                            description="Variables must start from {{1}} and be sequential ({{1}}, {{2}}, {{3}}, etc.)"
                            type="warning"
                            showIcon
                            size="small"
                            style={{ marginTop: "8px" }}
                          />
                        )}
                    </Form.Item>
                  </Col>
                  {/*<Col xs={24} md={8}>
                    <Form.Item
                      label="Footer Text"
                      help="Optional footer text"
                    >
                      <TextArea
                        rows={3}
                        placeholder="Optional footer text..."
                        value={templateData.footerText}
                        onChange={(e) => setTemplateData(prev => ({
                          ...prev,
                          footerText: e.target.value
                        }))}
                        maxLength={60}
                        showCount
                      />
                    </Form.Item>
                  </Col>*/}
                </Row>
              </Card>

              {/* Template Header (Common for all cards) */}
              <Card
                size="small"
                title="Template Header (Common for All Cards)"
                className="mb-3"
              >
                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '12px' }}>
                  <InfoCircleOutlined style={{ marginRight: '4px' }} />
                  A sample image is used here — original images will be provided at the time of sending the template.
                </Text>
                <Row gutter={ROW_GUTTER}>
                  <Col xs={24} md={8}>
                    <Form.Item label="Header Type" required>
                      <Select
                        value={templateImage.type}
                        onChange={updateTemplateHeaderType}
                      >
                        <Option value="IMAGE">Image</Option>
                        <Option value="VIDEO">Video</Option>
                      </Select>
                    </Form.Item>
                  </Col>

                  {templateImage.type === "TEXT" ? (
                    <Col xs={24} md={8}>
                      <Form.Item label="Header Text" required>
                        <Input
                          placeholder="Enter header text for all cards..."
                          value={templateImage.text || ""}
                          onChange={(e) =>
                            updateTemplateHeaderText(e.target.value)
                          }
                          maxLength={60}
                        />
                      </Form.Item>
                    </Col>
                  ) : (
                    <Col xs={24} md={8}>
                      <Form.Item
                        label={`Upload Template ${templateImage.type}`}
                        required
                      >
                        <Upload
                          beforeUpload={handleTemplateImageUpload}
                          showUploadList={false}
                          accept={
                            templateImage.type === "IMAGE"
                              ? ".jpg,.jpeg,.png"
                              : ".mp4"
                          }
                        >
                          <Button
                            icon={<UploadOutlined />}
                            loading={loading}
                            size="medium"
                          >
                            Select Template {templateImage.type}
                          </Button>
                        </Upload>
                        {templateImage.mediaUrl && (
                          <div className="mt-2">
                            <Tag color="green">
                              <CheckCircleOutlined /> Template{" "}
                              {templateImage.type.toLowerCase()} uploaded
                            </Tag>
                          </div>
                        )}
                        <div
                          style={{
                            marginTop: "8px",
                            fontSize: "12px",
                            color: "#666",
                          }}
                        >
                          {templateImage.type === "IMAGE"
                            ? "JPG or PNG format, max 5MB"
                            : "MP4 format, max 16MB"}
                        </div>
                      </Form.Item>
                    </Col>
                  )}
                  {/* Preview Section */}
                  {(templateImage.mediaUrl ||
                    (templateImage.type === "TEXT" && templateImage.text)) && (
                      <Col xs={24} md={8}>
                        <Text
                          strong
                          style={{ display: "block", marginBottom: "8px" }}
                        >
                          Header Preview (will be used for all cards):
                        </Text>
                        <div
                          style={{
                            border: "1px solid #d9d9d9",
                            borderRadius: "6px",
                            padding: "12px",
                            backgroundColor: "#fafafa",
                          }}
                        >
                          {templateImage.type === "TEXT" ? (
                            <Text strong>{templateImage.text}</Text>
                          ) : templateImage.type === "IMAGE" ? (
                            <img
                              src={templateImage.mediaUrl}
                              alt="Template Header Preview"
                              style={{
                                width: "100%",
                                maxHeight: "200px",
                                objectFit: "cover",
                                borderRadius: "4px",
                              }}
                            />
                          ) : (
                            <video
                              src={templateImage.mediaUrl}
                              style={{
                                width: "100%",
                                maxHeight: "200px",
                                borderRadius: "4px",
                              }}
                              controls
                            />
                          )}
                        </div>
                      </Col>
                    )}
                </Row>
              </Card>

              {/* Carousel Cards */}
              <Card
                size="small"
                title={
                  <Space>
                    <span>Carousel Cards ({cards.length}/10)</span>
                    <Tooltip title="Add new card">
                      <Button
                        type="dashed"
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={addCard}
                        disabled={cards.length >= 10}
                      >
                        Add Card
                      </Button>
                    </Tooltip>
                  </Space>
                }
                className="mb-3"
              >
                <Tabs type="card">
                  {cards.map((card, index) => (
                    <TabPane
                      tab={
                        <Space>
                          <span>Card {index + 1}</span>
                          {cards.length > 1 && (
                            <Tooltip title="Remove this card">
                              <Button
                                type="text"
                                danger
                                size="small"
                                icon={<DeleteOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeCard(card.id);
                                }}
                                style={{ marginLeft: "4px" }}
                              />
                            </Tooltip>
                          )}
                        </Space>
                      }
                      key={card.id}
                    >
                      <Row gutter={ROW_GUTTER}>
                        {/* Body Section */}
                        <Col xs={24}>
                          <Card size="small" title="Body" className="mb-3">
                            <Form.Item>
                              <TextArea
                                rows={3}
                                placeholder="Enter card body text... Use {{1}}, {{2}} for variables"
                                value={card.body}
                                onChange={(e) =>
                                  updateCard(card.id, "body", e.target.value)
                                }
                                maxLength={160}
                                showCount
                              />
                              <div
                                style={{
                                  marginTop: "8px",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Button
                                  size="small"
                                  icon={<PlusOutlined />}
                                  onClick={() =>
                                    addVariable(card.body, (newText) =>
                                      updateCard(card.id, "body", newText)
                                    )
                                  }
                                >
                                  Add Variable
                                </Button>
                                {extractVariables(card.body).length > 0 && (
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: "12px" }}
                                  >
                                    Variables:{" "}
                                    {extractVariables(card.body)
                                      .map((v) => `{{${v}}}`)
                                      .join(", ")}
                                  </Text>
                                )}
                              </div>
                              <Text className="text-warning">
                                Note: Keep your message short, when variables
                                are replaced with real values, the final text
                                must still fit in{" "}
                                <strong className="text-danger">160</strong>{" "}
                                characters.
                              </Text>
                              {card.body &&
                                !validateVariableSequence(card.body) && (
                                  <Alert
                                    message="Variable Sequence Error"
                                    description="Variables must start from {{1}} and be sequential"
                                    type="warning"
                                    showIcon
                                    size="small"
                                    style={{ marginTop: "8px" }}
                                  />
                                )}
                            </Form.Item>
                          </Card>
                        </Col>

                        {/* Buttons Section */}
                        <Col xs={24}>
                          <Card
                            size="small"
                            title={
                              <Space>
                                <span>Buttons ({card.buttons.length}/2)</span>
                                <Tooltip
                                  title={
                                    card.buttons.length >= 2
                                      ? "Maximum 2 buttons per card"
                                      : "Add another button to this card"
                                  }
                                >
                                  <Button
                                    type="dashed"
                                    size="small"
                                    icon={<PlusOutlined />}
                                    onClick={() => addButton(card.id)}
                                    disabled={card.buttons.length >= 2}
                                  >
                                    Add Button
                                  </Button>
                                </Tooltip>
                              </Space>
                            }
                            extra={
                              card.buttons.length > 0 && (
                                <Text
                                  type="secondary"
                                  style={{ fontSize: "12px" }}
                                >
                                  Mix button types • Max 1 call button • At
                                  least 1 required
                                </Text>
                              )
                            }
                          >
                            {card.buttons.length > 0 && (
                              <Alert
                                message="Button Requirements"
                                description="Each card requires at least 1 button (max 2). You can mix button types, but only 1 call button per card."
                                type="info"
                                showIcon
                                className="mb-3"
                                closable
                              />
                            )}

                            {card.buttons.map((button, btnIndex) => (
                              <Card
                                key={button.id}
                                size="small"
                                className="mb-2"
                                title={
                                  <Space>
                                    <span>Button {btnIndex + 1}</span>
                                    <Tag
                                      color={
                                        button.type === "QUICK_REPLY"
                                          ? "blue"
                                          : button.type === "URL"
                                            ? "green"
                                            : "orange"
                                      }
                                    >
                                      {button.type === "QUICK_REPLY"
                                        ? "Quick Reply"
                                        : button.type === "URL"
                                          ? "Visit Website"
                                          : "Call Phone"}
                                    </Tag>
                                  </Space>
                                }
                                extra={
                                  <Tooltip title="Remove this button">
                                    <Button
                                      type="text"
                                      danger
                                      size="small"
                                      icon={<DeleteOutlined />}
                                      onClick={() =>
                                        removeButton(card.id, button.id)
                                      }
                                    />
                                  </Tooltip>
                                }
                              >
                                <Row gutter={ROW_GUTTER}>
                                  <Col xs={24} md={8}>
                                    <Form.Item label="Button Type" required>
                                      <Select
                                        value={button.type}
                                        onChange={(value) =>
                                          updateButton(
                                            card.id,
                                            button.id,
                                            "type",
                                            value
                                          )
                                        }
                                        placeholder="Select button type"
                                      >
                                        {buttonTypes.map((type) => (
                                          <Option
                                            key={type.value}
                                            value={type.value}
                                          >
                                            <Space>
                                              {type.value === "QUICK_REPLY" &&
                                                "💬"}
                                              {type.value === "URL" && "🌐"}
                                              {type.value === "PHONE_NUMBER" &&
                                                "📞"}
                                              {type.label}
                                            </Space>
                                          </Option>
                                        ))}
                                      </Select>
                                    </Form.Item>
                                  </Col>
                                </Row>
                                <Row gutter={ROW_GUTTER}>
                                  <Col xs={24} md={8}>
                                    <Form.Item label="Button Text" required>
                                      <Input
                                        placeholder="Enter button text..."
                                        value={button.text}
                                        onChange={(e) =>
                                          updateButton(
                                            card.id,
                                            button.id,
                                            "text",
                                            e.target.value
                                          )
                                        }
                                        maxLength={20}
                                        showCount
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={16}>
                                    {button.type === "URL" && (
                                      <Row gutter={ROW_GUTTER}>
                                        <Col xs={24} md={12}>
                                          <Form.Item label="URL Type" required>
                                            <Select
                                              value={button.urlType || "STATIC"}
                                              onChange={(value) => {
                                                // Use a combined update to ensure proper state update
                                                const updatedButtons =
                                                  card.buttons.map((btn) =>
                                                    btn.id === button.id
                                                      ? {
                                                        ...btn,
                                                        urlType: value,
                                                        url: "",
                                                      }
                                                      : btn
                                                  );
                                                updateCard(
                                                  card.id,
                                                  "buttons",
                                                  updatedButtons
                                                );
                                              }}
                                              placeholder="Select URL type"
                                            >
                                              <Option value="STATIC">
                                                Static URL
                                              </Option>
                                              <Option value="DYNAMIC">
                                                Dynamic URL
                                              </Option>
                                            </Select>
                                          </Form.Item>
                                        </Col>
                                        <Col xs={24} md={12}>
                                          <Form.Item label="URL" required>
                                            <Input
                                              placeholder={
                                                button.urlType === "DYNAMIC"
                                                  ? "https://example.com/{{1}}"
                                                  : "https://example.com"
                                              }
                                              value={button.url}
                                              onChange={(e) =>
                                                updateButton(
                                                  card.id,
                                                  button.id,
                                                  "url",
                                                  e.target.value
                                                )
                                              }
                                              prefix="🌐"
                                            />

                                            {button.urlType === "DYNAMIC" && (
                                              <div
                                                style={{
                                                  marginTop: "4px",
                                                  display: "flex",
                                                  justifyContent:
                                                    "space-between",
                                                  alignItems: "center",
                                                }}
                                              >
                                                <Button
                                                  size="small"
                                                  icon={<PlusOutlined />}
                                                  onClick={() =>
                                                    addUrlVariable(
                                                      button.url,
                                                      (newUrl) =>
                                                        updateButton(
                                                          card.id,
                                                          button.id,
                                                          "url",
                                                          newUrl
                                                        )
                                                    )
                                                  }
                                                  disabled={button.url.includes(
                                                    "{{1}}"
                                                  )}
                                                >
                                                  Add Variable
                                                </Button>
                                                {button.url.includes(
                                                  "{{1}}"
                                                ) && (
                                                    <Text
                                                      type="secondary"
                                                      style={{ fontSize: "11px" }}
                                                    >
                                                      {" Variable: {{1}}"}
                                                    </Text>
                                                  )}
                                              </div>
                                            )}

                                            {button.urlType === "DYNAMIC" &&
                                              !button.url.includes("{{1}}") &&
                                              button.url && (
                                                <Alert
                                                  message="Dynamic URL Required"
                                                  description="Dynamic URLs must contain the {{1}} variable"
                                                  type="warning"
                                                  showIcon
                                                  size="small"
                                                  style={{ marginTop: "4px" }}
                                                />
                                              )}

                                            {button.urlType === "STATIC" &&
                                              extractVariables(button.url)
                                                .length > 0 && (
                                                <Alert
                                                  message="Static URL Error"
                                                  description="Static URLs cannot contain variables"
                                                  type="warning"
                                                  showIcon
                                                  size="small"
                                                  style={{ marginTop: "4px" }}
                                                />
                                              )}

                                            <Text
                                              type="secondary"
                                              style={{
                                                fontSize: "11px",
                                                display: "block",
                                                marginTop: "4px",
                                              }}
                                            >
                                              {button.urlType === "DYNAMIC"
                                                ? "💡 Dynamic URLs use {{1}} for personalization when sending messages"
                                                : "💡 Static URLs remain the same for all recipients"}
                                            </Text>
                                          </Form.Item>
                                        </Col>
                                      </Row>
                                    )}
                                    {button.type === "PHONE_NUMBER" && (
                                      <Form.Item label="Phone Number" required>
                                        <Input
                                          placeholder="+1234567890"
                                          value={button.phoneNumber}
                                          onChange={(e) =>
                                            updateButton(
                                              card.id,
                                              button.id,
                                              "phoneNumber",
                                              e.target.value
                                            )
                                          }
                                          prefix="📞"
                                        />
                                      </Form.Item>
                                    )}
                                    {button.type === "QUICK_REPLY" && (
                                      <Form.Item
                                        label="Action"
                                        help="Quick reply buttons send the text back to you"
                                      >
                                        <Input
                                          placeholder="No additional config needed"
                                          disabled
                                          prefix="💬"
                                        />
                                      </Form.Item>
                                    )}
                                  </Col>
                                </Row>
                              </Card>
                            ))}

                            {card.buttons.length === 0 && (
                              <Alert
                                message="At least 1 button required"
                                description={
                                  <div>
                                    <p>
                                      Each card must have at least 1 button.
                                      Choose from:
                                    </p>
                                    <ul
                                      style={{
                                        marginBottom: 0,
                                        paddingLeft: "20px",
                                      }}
                                    >
                                      <li>
                                        <strong>Quick Reply:</strong> Users can
                                        quickly respond with predefined text
                                      </li>
                                      <li>
                                        <strong>Visit Website:</strong> Direct
                                        users to your website or landing page
                                      </li>
                                      <li>
                                        <strong>Call Phone:</strong> Allow users
                                        to call your business directly (max 1
                                        per card)
                                      </li>
                                    </ul>
                                    <p
                                      style={{
                                        marginTop: "8px",
                                        marginBottom: 0,
                                      }}
                                    >
                                      <strong>Limit:</strong> Maximum 2 buttons
                                      per card, with only 1 call button allowed.
                                    </p>
                                  </div>
                                }
                                type="warning"
                                showIcon
                              />
                            )}
                          </Card>
                        </Col>
                      </Row>
                    </TabPane>
                  ))}
                </Tabs>
              </Card>
            </Form>
          </Card>
        </Col>

        {/* Guidelines and Tips */}
        <Col xs={24} lg={8}>
          <Flex className="justify-content-between mb-2">
            <Title level={4} className="mb-2">
              Preview
            </Title>
            <Switch
              defaultChecked={isLoop}
              onChange={(e) => setIsLoop(e.target.checked)}
            />
          </Flex>
          <CarouselPreview
            mainBody={templateData}
            requestType={"template"}
            cards={cards}
            isLoop={isLoop}
            requiredMedia={false}
          />
          <Card title="Guidelines & Tips" className="my-2" size="small">
            <Collapse defaultActiveKey={["1"]} size="small">
              <Panel header="Template Requirements" key="1">
                <List size="small">
                  <List.Item>
                    <InfoCircleOutlined className="mr-2" />
                    Template name must be unique and use lowercase letters,
                    numbers, and underscores only
                  </List.Item>
                  <List.Item>
                    <InfoCircleOutlined className="mr-2" />
                    Body text is required and limited to 1024 characters
                  </List.Item>
                  <List.Item>
                    <InfoCircleOutlined className="mr-2" />
                    Maximum 10 cards per carousel template
                  </List.Item>
                  <List.Item>
                    <InfoCircleOutlined className="mr-2" />
                    Each card requires at least 1 button (max 2)
                  </List.Item>
                  <List.Item>
                    <InfoCircleOutlined className="mr-2" />
                    Maximum 1 call button per card
                  </List.Item>
                </List>
              </Panel>

              <Panel header="Media Guidelines" key="2">
                <List size="small">
                  <List.Item>
                    <InfoCircleOutlined className="mr-2" />
                    Images: JPG, PNG formats, max 5MB
                  </List.Item>
                  <List.Item>
                    <InfoCircleOutlined className="mr-2" />
                    Videos: MP4 format, max 16MB
                  </List.Item>
                  <List.Item>
                    <InfoCircleOutlined className="mr-2" />
                    Recommended image size: 1200x628 pixels
                  </List.Item>
                </List>
              </Panel>

              <Panel header="Button Types & Requirements" key="3">
                <Alert
                  message="Button Requirements"
                  description="Each card requires at least 1 button (max 2). You can mix button types, but only 1 call button per card."
                  type="info"
                  showIcon
                  className="mb-3"
                />
                <List size="small">
                  <List.Item>
                    <InfoCircleOutlined className="mr-2" />
                    <strong>💬 Quick Reply:</strong> Simple text response -
                    users tap to send predefined text
                  </List.Item>
                  <List.Item>
                    <InfoCircleOutlined className="mr-2" />
                    <strong>🌐 Visit Website:</strong> Opens URL in browser -
                    direct users to your website
                  </List.Item>
                  <List.Item>
                    <InfoCircleOutlined className="mr-2" />
                    <strong>📞 Call Phone:</strong> Initiates phone call - let
                    users call your business
                  </List.Item>
                  <List.Item>
                    <InfoCircleOutlined className="mr-2" />
                    <strong>Example:</strong> Card can have "Learn More" (URL) +
                    "Call Us" (Phone) - max 2 buttons total
                  </List.Item>
                </List>
              </Panel>

              <Panel header="Dynamic Variables Support" key="4">
                <Alert
                  message="Dynamic Content with Variables"
                  description="Use {{1}}, {{2}}, {{3}} etc. to create personalized templates that can be filled with dynamic data when sending messages."
                  type="info"
                  showIcon
                  className="mb-3"
                />
                <List size="small">
                  <List.Item>
                    <InfoCircleOutlined className="mr-2" />
                    <strong>Body Text Variables:</strong> "Hello {"{1}"}, your
                    order {"{2}"} is ready!"
                  </List.Item>
                  <List.Item>
                    <InfoCircleOutlined className="mr-2" />
                    <strong>Card Body Variables:</strong> "Product: {"{1}"} -
                    Price: ${"{2}"}"
                  </List.Item>
                  <List.Item>
                    <InfoCircleOutlined className="mr-2" />
                    <strong>URL Variables:</strong> "https://shop.com/product/
                    {"{1}"}?user={"{2}"}"
                  </List.Item>
                  <List.Item>
                    <InfoCircleOutlined className="mr-2" />
                    <strong>Rules:</strong> Variables must start from {"{1}"}{" "}
                    and be sequential ({"{1}"}, {"{2}"}, {"{3}"}, etc.)
                  </List.Item>
                  <List.Item>
                    <InfoCircleOutlined className="mr-2" />
                    <strong>Tip:</strong> Use "Add Variable" buttons to
                    automatically insert the next sequential variable
                  </List.Item>
                </List>
              </Panel>
            </Collapse>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CarouselTemplateCreator;
